// /src/pages/private/settings/ExtensionSettings.jsx

import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import PrivateLexicalPage from '../../../components/PrivateLexicalPage';
import chromeExtension from '../../../medias/chromeExtension.png';
import copyIcon from '../../../medias/copy.svg';
import copiedIcon from '../../../medias/copied.svg';

export function ExtensionSettings({ onTocReady, onTocVisibleChange }) {
    const { t } = useTranslation();
    const [token, setToken] = useState('');
    const [isCopied, setIsCopied] = useState(false);
    const [globalError, setGlobalError] = useState('');
    const textareaRef = useRef(null);

    // English comment: load existing extension token on page load
    useEffect(() => {
        retrieveExtToken();
    }, []);

    // English comment: copy token via icon
    const copyTokenToClipboard = () => {
        if (navigator.clipboard && token) {
            navigator.clipboard.writeText(token)
                .then(() => {
                    setGlobalError('Token copied to clipboard!');
                    setIsCopied(true);
                    setTimeout(() => setIsCopied(false), 5000);
                })
                .catch(err => console.error('Could not copy text: ', err));
        }
    };

    // English comment: retrieve token from backend
    const retrieveExtToken = async () => {
        try {
            const response = await axios.post(
                '/api/extension/retrieveExtensionToken',
                {},
                { withCredentials: true }
            );

            if (response.status === 200 && response.data.token) {
                setToken(response.data.token);
            }
        } catch (error) {
            if (error.response && error.response.status !== 404) {
                console.error("Error retrieving extension token:", error);
            }
        }
    };

    // English comment: generate or regenerate token
    const requestExtToken = async () => {
        try {
            const response = await axios.post(
                '/api/extension/createExtensionToken',
                {},
                { withCredentials: true }
            );

            if (response.status === 200) {
                setToken(response.data.token);
            }
        } catch (error) {
            console.error("Erreur lors de la génération du token:", error);
        }
    };

    // English comment: open Chrome Web Store page
    const installExtension = () => {
        const extensionUrl =
            'https://chromewebstore.google.com/detail/pallas-invictus-sync-tool/loaalmlekagkninjfdeoknlipmnopfhh';
        window.open(extensionUrl, '_blank', 'noopener,noreferrer');
    };

    return (
        <div>
            <div className="marging-small-spacer"></div>

            <PrivateLexicalPage 
                documentName="Extension"
                layout="plain"
                onTocReady={onTocReady}
                onTocVisibleChange={onTocVisibleChange}
            />

            <div className="extension-setup-steps">
                <h3>{t('Extension Setup Steps')}</h3>
                <ol className="extension-setup-list">
                    <li>Install the extension</li>
                    <li>Generate the key</li>
                    <li>Copy the key</li>
                    <li>Open the extension options</li>
                    <li>Paste the key in the identification token field</li>
                </ol>
            </div>

            <div className="textarea-container">
                <button
                    className="btn-style1 btn-extension-token"
                    onClick={requestExtToken}
                >
                    {token
                        ? t('Regenerate a key')
                        : t('Generate a key')
                    }
                </button>


                <textarea
                    ref={textareaRef}
                    className="extension-token"
                    value={token}
                    readOnly
                    onClick={() => textareaRef.current?.select()}
                    onKeyDown={(e) => e.preventDefault()}
                />

                <img
                    src={isCopied ? copiedIcon : copyIcon}
                    alt="Copy token"
                    className="copy-key-icon"
                    onClick={copyTokenToClipboard}
                />

                <button
                    className="btn-download-extension glass-card2"
                    onClick={installExtension}
                >
                    <img
                        src={chromeExtension}
                        alt="Chrome extension"
                        className="btn-download-extension-icon"
                    />

                    <div className="btn-download-extension-text">
                        <span className="btn-download-extension-title">
                            Pallas-Invictus - Synchronization Tool
                        </span>
                        <span className="btn-download-extension-subtitle">
                            The extension is available on the Chrome Store through this link only
                        </span>
                    </div>
                </button>

            </div>
        </div>
    );
}
